import React from 'react';
import {
  GlobeAltIcon,
  TruckIcon,
  BuildingOffice2Icon,
  ClockIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  BoltIcon,
  DocumentCheckIcon,
  ServerIcon,
  CubeTransparentIcon,
} from '@heroicons/react/24/outline';
import { SolutionPageTemplate } from '../../components/page-templates/MarketPageTemplates';

const painPoints = [
  {
    icon: DocumentCheckIcon,
    title: 'Regulatory Complexity',
    description: 'Different import rules, certifications, and duties in every market',
  },
  {
    icon: ClockIcon,
    title: 'Project-Based Timelines',
    description: 'Site openings and installations run on fixed dates with no room for delay',
  },
  {
    icon: UserGroupIcon,
    title: 'Multiple Vendors Across Regions',
    description: 'Too many forwarders, brokers, and warehouses with no single point of accountability',
  },
  {
    icon: ExclamationTriangleIcon,
    title: 'On-Site Delivery Risk',
    description: 'Equipment must arrive on time and be positioned correctly at the destination',
  },
];

const solutions = [
  {
    icon: DocumentCheckIcon,
    title: 'Regulatory & Import Compliance',
    points: [
      'IOR/EOR services across regulated markets',
      'Duties and taxes management',
      'Faster clearance and processing',
      'Market entry without local entity setup',
    ],
  },
  {
    icon: TruckIcon,
    title: 'Project-Based Freight & Consolidation',
    points: [
      'Consolidated shipments for large rollouts',
      'Phased delivery aligned to project milestones',
      'Secure handling of high-value equipment',
      'Coordinated cross-border movement',
    ],
  },
  {
    icon: BuildingOffice2Icon,
    title: 'Regional Warehousing & Staging',
    points: [
      'Regional stock positioning near deployment sites',
      'Equipment inspection and validation',
      'Pre-installation preparation and testing',
      'Flexible redistribution across locations',
    ],
  },
  {
    icon: CubeTransparentIcon,
    title: 'White Glove Delivery & On-Site Coordination',
    points: [
      'Scheduled delivery to exact specifications',
      'Inside delivery and precise placement',
      'Installation support coordination',
      'Packaging removal and site cleanup',
    ],
  },
];

const whyChooseCards = [
  {
    icon: GlobeAltIcon,
    title: 'Single Global Partner',
    description: 'One accountable execution layer across 140+ countries, replacing fragmented vendor networks.',
    accent: 'bg-blue-500',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Regulatory Confidence',
    description: 'Structured compliance management across regulated markets with full audit trails.',
    accent: 'bg-cyan-500',
  },
  {
    icon: BoltIcon,
    title: 'Operational Focus',
    description: 'Your team focuses on operations and revenue while we manage movement and control.',
    accent: 'bg-indigo-500',
  },
];

const HyperscalersCloud: React.FC = () => (
  <SolutionPageTemplate
    eyebrow="Hyperscalers & Cloud Providers"
    title="One Accountable Partner for Global Equipment Movement"
    highlight="Global Equipment Movement"
    description="TrueLog supports organisations moving regulated, high-value, or project-critical equipment across borders with compliance, precision, and scale."
    heroIcon={ServerIcon}
    heroAccentIcon={GlobeAltIcon}
    problemHeading="The challenges you face"
    problemDescription="Moving high-value equipment at scale means navigating regulatory, operational, and coordination complexity simultaneously."
    painPoints={painPoints}
    solutionsIntro="TrueLog gives you one accountable partner for every stage of regulated equipment movement."
    solutions={solutions}
    whyChooseCards={whyChooseCards}
    ctaTitle="Ready to simplify your global equipment logistics?"
    ctaDescription="Let TrueLog move your project-critical infrastructure with the compliance confidence and visibility your teams need."
  />
);

export default HyperscalersCloud;
